import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  FiKey, FiCamera, FiUpload, FiPlus, FiCopy, FiTrash2,
  FiCheck, FiX, FiShield, FiSmartphone,
  FiDownload, FiSearch, FiImage
} from 'react-icons/fi';
import jsQR from 'jsqr';
import axios from 'axios';
import { API_BASE } from '../services/api';
import WebAuthnManager from './WebAuthnManager';
import secureStorage from '../services/secureStorage';
import './OTPManager.css';

// TOTP implementation
const base32Decode = (input) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let bits = '';
  let output = [];
  
  const cleanInput = input.toUpperCase().replace(/[^A-Z2-7]/g, '');
  
  for (let i = 0; i < cleanInput.length; i++) {
    const val = alphabet.indexOf(cleanInput[i]);
    if (val === -1) continue;
    bits += val.toString(2).padStart(5, '0');
  }
  
  for (let i = 0; i + 8 <= bits.length; i += 8) {
    output.push(parseInt(bits.substr(i, 8), 2));
  }
  
  return new Uint8Array(output);
};

const hmacSha1 = async (key, message) => {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    { name: 'HMAC', hash: 'SHA-1' },
    false,
    ['sign']
  );
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, message);
  return new Uint8Array(signature);
};

const generateTOTP = async (secret, period = 30, digits = 6) => {
  try {
    const key = base32Decode(secret);
    const time = Math.floor(Date.now() / 1000 / period);
    
    const timeBuffer = new ArrayBuffer(8);
    const timeView = new DataView(timeBuffer);
    timeView.setUint32(4, time, false);
    
    const hmac = await hmacSha1(key, new Uint8Array(timeBuffer));
    const offset = hmac[hmac.length - 1] & 0x0f;
    
    const code = (
      ((hmac[offset] & 0x7f) << 24) |
      ((hmac[offset + 1] & 0xff) << 16) |
      ((hmac[offset + 2] & 0xff) << 8) |
      (hmac[offset + 3] & 0xff)
    ) % Math.pow(10, digits);
    
    return code.toString().padStart(digits, '0');
  } catch (e) {
    console.error('Error generating TOTP:', e);
    return null;
  }
};

const parseOtpAuthUrl = (url) => {
  try {
    const match = url.match(/^otpauth:\/\/(totp|hotp)\/([^?]+)\?(.+)$/i);
    if (!match) return null;
    
    const type = match[1].toLowerCase();
    const label = decodeURIComponent(match[2]);
    const params = new URLSearchParams(match[3]);
    
    const [issuer, account] = label.includes(':') 
      ? label.split(':').map(s => s.trim())
      : [params.get('issuer') || '', label];
    
    return {
      type,
      issuer: params.get('issuer') || issuer,
      account,
      secret: params.get('secret'),
      algorithm: params.get('algorithm') || 'SHA1',
      digits: parseInt(params.get('digits')) || 6,
      period: parseInt(params.get('period')) || 30,
      counter: parseInt(params.get('counter')) || 0
    };
  } catch (e) {
    return null;
  }
};

// Parse Google Authenticator migration format (otpauth-migration://offline?data=...)
const parseGoogleAuthMigration = (url) => {
  try {
    if (!url.startsWith('otpauth-migration://')) return null;
    
    const dataMatch = url.match(/data=([^&]+)/);
    if (!dataMatch) return null;
    
    // Decode the base64 data
    const base64Data = decodeURIComponent(dataMatch[1]);
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    // Parse the protobuf (simplified parser for Google Authenticator format)
    const entries = [];
    let pos = 0;
    
    while (pos < bytes.length) {
      // Field 1: otp_parameters (repeated message)
      if (bytes[pos] === 0x0a) { // field 1, wire type 2 (length-delimited)
        pos++;
        const len = bytes[pos++];
        const entryEnd = pos + len;
        
        const entry = {
          secret: '',
          issuer: '',
          account: '',
          type: 'totp',
          digits: 6,
          algorithm: 'SHA1'
        };
        
        while (pos < entryEnd) {
          const fieldTag = bytes[pos++];
          const fieldNum = fieldTag >> 3;
          const wireType = fieldTag & 0x07;
          
          if (wireType === 2) { // length-delimited (string/bytes)
            const fieldLen = bytes[pos++];
            const fieldData = bytes.slice(pos, pos + fieldLen);
            pos += fieldLen;
            
            if (fieldNum === 1) { // secret (bytes)
              // Convert to base32
              entry.secret = bytesToBase32(fieldData);
            } else if (fieldNum === 2) { // name/account
              entry.account = new TextDecoder().decode(fieldData);
            } else if (fieldNum === 3) { // issuer
              entry.issuer = new TextDecoder().decode(fieldData);
            }
          } else if (wireType === 0) { // varint
            let value = 0;
            let shift = 0;
            while (bytes[pos] & 0x80) {
              value |= (bytes[pos++] & 0x7f) << shift;
              shift += 7;
            }
            value |= bytes[pos++] << shift;
            
            if (fieldNum === 4) { // algorithm
              entry.algorithm = value === 2 ? 'SHA256' : value === 3 ? 'SHA512' : 'SHA1';
            } else if (fieldNum === 5) { // digits
              entry.digits = value === 2 ? 8 : 6;
            } else if (fieldNum === 6) { // type
              entry.type = value === 1 ? 'hotp' : 'totp';
            }
          }
        }
        
        if (entry.secret) {
          // Extract issuer from account if format is "issuer:account"
          if (entry.account.includes(':')) {
            const parts = entry.account.split(':');
            if (!entry.issuer) entry.issuer = parts[0].trim();
            entry.account = parts[1].trim();
          }
          entries.push(entry);
        }
      } else {
        pos++;
      }
    }
    
    return entries;
  } catch (e) {
    console.error('Error parsing Google Auth migration:', e);
    return null;
  }
};

// Convert bytes to base32
const bytesToBase32 = (bytes) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let bits = '';
  let result = '';
  
  for (let i = 0; i < bytes.length; i++) {
    bits += bytes[i].toString(2).padStart(8, '0');
  }
  
  for (let i = 0; i + 5 <= bits.length; i += 5) {
    const chunk = bits.substr(i, 5);
    result += alphabet[parseInt(chunk, 2)];
  }
  
  // Pad to multiple of 8
  while (result.length % 8 !== 0) {
    result += '=';
  }
  
  return result;
};

const OTP_CACHE_VERSION = 'v2';

const normalizeOtpEntries = (entries = []) => {
  if (!Array.isArray(entries)) return [];
  return [...entries]
    .map(entry => ({
      id: entry.id || '',
      issuer: entry.issuer || '',
      account: entry.account || '',
      secret: entry.secret || '',
      type: entry.type || 'totp',
      digits: entry.digits || 6,
      period: entry.period || 30,
      algorithm: entry.algorithm || 'SHA1',
      counter: entry.counter || 0,
      createdAt: entry.createdAt || ''
    }))
    .sort((a, b) => (a.id || '').localeCompare(b.id || ''));
};

const buildOtpSignature = (entries = []) => JSON.stringify(normalizeOtpEntries(entries));

const OTPManager = ({ onClose, onSave }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('totp'); // 'totp' or 'webauthn'
  const [otpEntries, setOtpEntries] = useState([]);
  const [codes, setCodes] = useState({});
  const [timeLeft, setTimeLeft] = useState(30);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showImportScanModal, setShowImportScanModal] = useState(false);
  const [importedFromQR, setImportedFromQR] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  
  // Add entry form
  const [newEntry, setNewEntry] = useState({
    issuer: '',
    account: '',
    secret: '',
    type: 'totp',
    digits: 6,
    period: 30
  });
  
  // Camera refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Import camera refs
  const importVideoRef = useRef(null);
  const importCanvasRef = useRef(null);
  const importStreamRef = useRef(null);
  const importQRInputRef = useRef(null);
  
  // Track the DB item ID for the OTP vault record
  const otpDbIdRef = useRef(null);
  const loadedRef = useRef(false);
  const saveTimeoutRef = useRef(null);
  const isDirtyRef = useRef(false);
  const lastSavedSignatureRef = useRef('[]');
  
  const persistOtpCache = useCallback(async (entries, metadata = {}) => {
    if (!secureStorage.isReady()) return;
    await secureStorage.setItem('otp_entries_cache', entries);
    await secureStorage.setItem('otp_entries_cache_timestamp', Date.now());
    await secureStorage.setItem('otp_entries_cache_version', OTP_CACHE_VERSION);
    if (metadata.itemId) await secureStorage.setItem('otp_entries_item_id', metadata.itemId);
    if (metadata.itemUpdatedAt) await secureStorage.setItem('otp_entries_item_updated_at', metadata.itemUpdatedAt);
    if (metadata.lastGlobalUpdate) await secureStorage.setItem('otp_entries_last_global_update', metadata.lastGlobalUpdate);
  }, []);

  const clearOtpCache = useCallback(async () => {
    if (!secureStorage.isReady()) return;
    secureStorage.removeItem('otp_entries_cache');
    secureStorage.removeItem('otp_entries_cache_timestamp');
    secureStorage.removeItem('otp_entries_cache_version');
    secureStorage.removeItem('otp_entries_item_id');
    secureStorage.removeItem('otp_entries_item_updated_at');
    secureStorage.removeItem('otp_entries_last_global_update');
  }, []);

  // Load OTP entries from secure cache + backend sync
  useEffect(() => {
    const loadFromCache = async () => {
      let cachedEntries = null;
      let metadata = null;
      try {
        if (secureStorage.isReady()) {
          const cacheVersion = await secureStorage.getItem('otp_entries_cache_version');
          if (cacheVersion && cacheVersion !== OTP_CACHE_VERSION) {
            await clearOtpCache();
            return { cachedEntries: null, metadata: null };
          }

          const cached = await secureStorage.getItem('otp_entries_cache');
          const cachedItemId = await secureStorage.getItem('otp_entries_item_id');
          const cachedItemUpdatedAt = await secureStorage.getItem('otp_entries_item_updated_at');
          const cachedLastGlobalUpdate = await secureStorage.getItem('otp_entries_last_global_update');

          metadata = {
            itemId: cachedItemId || null,
            itemUpdatedAt: cachedItemUpdatedAt || null,
            lastGlobalUpdate: cachedLastGlobalUpdate || null
          };

          if (Array.isArray(cached)) {
            setOtpEntries(cached);
            lastSavedSignatureRef.current = buildOtpSignature(cached);
            cachedEntries = cached;
          }
        }
      } catch (e) {
        console.warn('Error loading OTP cache:', e);
      }
      return { cachedEntries, metadata };
    };

    const isOtpItem = (item) => item && (item.name === '__otp_entries__' || item.data?.type === 'otp_vault');
    const toMeta = (found) => ({ id: found.id || found._id, updatedAt: found.updatedAt || null });
    // One retry with a short pause: IIS/ARR occasionally answers 502 on a
    // reused keep-alive socket; the request itself is fine.
    const getWithRetry = async (url, token) => {
      const headers = { Authorization: `Bearer ${token}` };
      try {
        return await axios.get(url, { headers });
      } catch (e) {
        const status = e?.response?.status;
        if (status === 502 || status === 503 || status === 504 || !status) {
          await new Promise(r => setTimeout(r, 800));
          return axios.get(url, { headers });
        }
        throw e;
      }
    };

    const findOtpItemMeta = async (rfidId, token) => {
      const base = `${API_BASE}/api/data/${rfidId}`;

      // 1) Server-side search by name: one request instead of walking the
      //    whole vault (1300+ items = 14 pages, each a chance to fail).
      try {
        const res = await getWithRetry(`${base}?light=1&search=__otp_entries__&limit=10`, token);
        const found = (res.data?.data || []).find(isOtpItem);
        if (found) return toMeta(found);
      } catch (e) {
        console.warn('OTP: search lookup failed, falling back to paging', e?.message);
      }

      // 2) Fallback: page through (legacy items typed otp_vault but not named).
      let page = 1;
      const limit = 100;
      while (page <= 200) {
        const listResponse = await getWithRetry(`${base}?light=1&page=${page}&limit=${limit}`, token);
        const pageItems = listResponse.data?.data || [];
        const found = (Array.isArray(pageItems) ? pageItems : []).find(isOtpItem);
        if (found) return toMeta(found);
        if (!listResponse.data?.hasMore || pageItems.length === 0) break;
        page += 1;
      }

      return null;
    };

    const syncFromDB = async (cachedEntries, cacheMeta) => {
      if (loadedRef.current) return;
      loadedRef.current = true;
      
      const rfidId = localStorage.getItem('rfidId');
      const token = localStorage.getItem('authToken');
      if (!rfidId || !token) return;
      
      try {
        const checkResponse = await axios.get(`${API_BASE}/api/data/${rfidId}/check`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const checkData = checkResponse.data || {};
        const serverLastGlobalUpdate = checkData.lastGlobalUpdate || null;

        // Fast path: cache is still current
        if (
          Array.isArray(cachedEntries) &&
          cacheMeta?.lastGlobalUpdate &&
          serverLastGlobalUpdate &&
          cacheMeta.lastGlobalUpdate === serverLastGlobalUpdate
        ) {
          return;
        }

        let otpItemMeta = null;
        const serverItems = Array.isArray(checkData.items) ? checkData.items : [];
        if (cacheMeta?.itemId) {
          const matched = serverItems.find(i => i.id === cacheMeta.itemId);
          if (matched) {
            otpItemMeta = {
              id: matched.id,
              updatedAt: matched.updatedAt || null
            };

            if (
              Array.isArray(cachedEntries) &&
              cacheMeta.itemUpdatedAt &&
              matched.updatedAt &&
              cacheMeta.itemUpdatedAt === matched.updatedAt
            ) {
              await persistOtpCache(cachedEntries, {
                itemId: matched.id,
                itemUpdatedAt: matched.updatedAt,
                lastGlobalUpdate: serverLastGlobalUpdate
              });
              return;
            }
          }
        }

        if (!otpItemMeta) {
          otpItemMeta = await findOtpItemMeta(rfidId, token);
        }

        // No OTP vault in DB
        if (!otpItemMeta?.id) {
          otpDbIdRef.current = null;
          if (!Array.isArray(cachedEntries) || cachedEntries.length === 0) {
            setOtpEntries([]);
            lastSavedSignatureRef.current = '[]';
          }
          await clearOtpCache();
          return;
        }

        // Fetch decrypted OTP vault only when needed
        const detail = await axios.get(`${API_BASE}/api/data/item/${otpItemMeta.id}`, {
          params: { rfidId },
          headers: { Authorization: `Bearer ${token}` }
        });

        otpDbIdRef.current = otpItemMeta.id;
        const entries = detail.data?.data?.entries || [];

        setOtpEntries(Array.isArray(entries) ? entries : []);
        lastSavedSignatureRef.current = buildOtpSignature(Array.isArray(entries) ? entries : []);
        await persistOtpCache(Array.isArray(entries) ? entries : [], {
          itemId: otpItemMeta.id,
          itemUpdatedAt: otpItemMeta.updatedAt || detail.data?.updatedAt || null,
          lastGlobalUpdate: serverLastGlobalUpdate
        });
      } catch (e) {
        console.error('Error loading OTP entries from DB:', e);
        // Fallback: try localStorage for migration
        const saved = localStorage.getItem('otpEntries');
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            if (parsed.length > 0) {
              setOtpEntries(parsed);
              if (secureStorage.isReady()) {
                await persistOtpCache(parsed);
                localStorage.removeItem('otpEntries');
              }
              lastSavedSignatureRef.current = buildOtpSignature(parsed);
              // Will be saved to DB by the save effect below
            }
          } catch (parseErr) {
            console.error('Error parsing localStorage OTP entries:', parseErr);
          }
        }
      }
    };

    loadFromCache().then(({ cachedEntries, metadata }) => syncFromDB(cachedEntries, metadata));
  }, [clearOtpCache, persistOtpCache]);
  
  // Save OTP entries to database (encrypted, debounced) and sync with extension
  const saveToDB = useCallback(async (entries) => {
    const rfidId = localStorage.getItem('rfidId');
    const token = localStorage.getItem('authToken');
    if (!rfidId || !token) return;

    // If vault is now empty, delete persisted OTP item and clear cache
    if (!Array.isArray(entries) || entries.length === 0) {
      if (otpDbIdRef.current) {
        try {
          await axios.delete(`${API_BASE}/api/data/${otpDbIdRef.current}`, {
            params: { rfidId },
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (e) {
          console.error('Error deleting OTP record:', e);
        }
        otpDbIdRef.current = null;
      }

      await clearOtpCache();
      localStorage.removeItem('otpEntries');
      return;
    }
    
    try {
      const saveResponse = await axios.post(`${API_BASE}/api/data/save`, {
        rfidId,
        name: '__otp_entries__',
        description: 'OTP Authenticator entries (encrypted)',
        data: { type: 'otp_vault', entries },
        tags: ['otp', 'system'],
        ...(otpDbIdRef.current ? { itemId: otpDbIdRef.current } : {})
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // After first save, get the ID for subsequent updates
      if (!otpDbIdRef.current) {
        const listRes = await axios.get(`${API_BASE}/api/data/${rfidId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const listItems = listRes.data?.data || listRes.data || [];
        const otpItem = (Array.isArray(listItems) ? listItems : []).find(item => item.name === '__otp_entries__');
        if (otpItem) otpDbIdRef.current = otpItem.id || otpItem._id;
      }
      
      // Update encrypted cache
      await persistOtpCache(entries, {
        itemId: otpDbIdRef.current,
        itemUpdatedAt: saveResponse?.data?.updatedAt || saveResponse?.data?.createdAt || null
      });

      // Clean up localStorage after successful DB save (migration complete)
      localStorage.removeItem('otpEntries');
    } catch (e) {
      console.error('Error saving OTP entries to DB:', e);
    }
  }, [clearOtpCache, persistOtpCache]);
  
  useEffect(() => {
    const signature = buildOtpSignature(otpEntries);

    // La extension ya no recibe los secretos 2FA (design/SETTINGS-DEVELOPER-PRIVACY.md 5.3.1): cuando
    // los necesita pide el codigo actual al servidor (GET /api/otp/code). Antes se empujaban aqui por
    // postMessage y la extension los guardaba en claro en chrome.storage.local.

    // Persist only when user changed OTP entries
    if (isDirtyRef.current && signature !== lastSavedSignatureRef.current) {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(async () => {
        await saveToDB(otpEntries);
        lastSavedSignatureRef.current = buildOtpSignature(otpEntries);
        isDirtyRef.current = false;
      }, 600);
    }
    
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [otpEntries, saveToDB]);
  
  // Generate codes for all entries.
  // hasCodesRef tracks whether we already have codes WITHOUT putting `codes`
  // in the timer effect's deps: with `codes` as a dep, every setCodes({}) on an
  // empty vault created a new object reference → effect re-ran → updateTimer()
  // → setCodes({}) again, synchronously, forever. That render loop starved the
  // async entry load and OOM'd the tab (no console error, React only warns
  // about update depth in render, not in effects).
  const hasCodesRef = useRef(false);
  const generateAllCodes = useCallback(async () => {
    const newCodes = {};
    for (const entry of otpEntries) {
      if (entry.type === 'totp') {
        const code = await generateTOTP(entry.secret, entry.period, entry.digits);
        if (code) {
          newCodes[entry.id] = code;
        }
      }
    }
    hasCodesRef.current = Object.keys(newCodes).length > 0;
    // Keep the previous reference when both are empty so an empty vault
    // doesn't re-render every second for nothing.
    setCodes(prev => (
      Object.keys(prev).length === 0 && !hasCodesRef.current ? prev : newCodes
    ));
  }, [otpEntries]);

  // Update codes every second
  useEffect(() => {
    const updateTimer = () => {
      const now = Math.floor(Date.now() / 1000);
      const remaining = 30 - (now % 30);
      setTimeLeft(remaining);

      if (remaining === 30 || !hasCodesRef.current) {
        generateAllCodes();
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [generateAllCodes]);
  
  // Start camera for QR scanning
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        scanQRCode();
      }
    } catch (e) {
      console.error('Error accessing camera:', e);
      alert(t('otp.cameraError'));
    }
  };
  
  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };
  
  // Scan QR code from video
  const scanQRCode = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const scan = () => {
      if (!streamRef.current) return;
      
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        
        if (code && code.data) {
          const parsed = parseOtpAuthUrl(code.data);
          if (parsed) {
            stopCamera();
            setShowScanModal(false);
            addOtpEntry(parsed);
            return;
          }
        }
      }
      
      requestAnimationFrame(scan);
    };
    
    scan();
  };
  
  // Handle image upload for QR scanning
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        
        if (code && code.data) {
          // Try Google Auth migration format first
          const migrationEntries = parseGoogleAuthMigration(code.data);
          if (migrationEntries && migrationEntries.length > 0) {
            migrationEntries.forEach(entry => addOtpEntry(entry));
            return;
          }
          
          // Try standard otpauth format
          const parsed = parseOtpAuthUrl(code.data);
          if (parsed) {
            addOtpEntry(parsed);
          } else {
            alert(t('otp.invalidQR'));
          }
        } else {
          alert(t('otp.noQRFound'));
        }
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };
  
  // Start import camera for QR scanning (Google Authenticator export)
  const startImportCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      importStreamRef.current = stream;
      if (importVideoRef.current) {
        importVideoRef.current.srcObject = stream;
        importVideoRef.current.play();
        scanImportQRCode();
      }
    } catch (e) {
      console.error('Error accessing camera:', e);
      alert(t('otp.cameraError'));
    }
  };
  
  // Stop import camera
  const stopImportCamera = () => {
    if (importStreamRef.current) {
      importStreamRef.current.getTracks().forEach(track => track.stop());
      importStreamRef.current = null;
    }
  };

  // Release both cameras if the modal unmounts while scanning. The rAF scan
  // loops only stop when the stream ref is null, so without this the closure
  // keeps grabbing getImageData() frames after the component is gone.
  useEffect(() => {
    return () => {
      stopCamera();
      stopImportCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scan QR code from import camera (handles Google Auth migration format)
  const scanImportQRCode = () => {
    if (!importVideoRef.current || !importCanvasRef.current) return;
    
    const video = importVideoRef.current;
    const canvas = importCanvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const scan = () => {
      if (!importStreamRef.current) return;
      
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        
        if (code && code.data) {
          // Try Google Auth migration format
          const migrationEntries = parseGoogleAuthMigration(code.data);
          if (migrationEntries && migrationEntries.length > 0) {
            stopImportCamera();
            setImportedFromQR(prev => [...prev, ...migrationEntries]);
            // Show success notification
            alert(t('otp.qrScanned', { count: migrationEntries.length }));
            return;
          }
          
          // Try standard otpauth format
          const parsed = parseOtpAuthUrl(code.data);
          if (parsed) {
            stopImportCamera();
            setImportedFromQR(prev => [...prev, parsed]);
            alert(t('otp.qrScanned', { count: 1 }));
            return;
          }
        }
      }
      
      requestAnimationFrame(scan);
    };
    
    scan();
  };
  
  // Handle import QR image upload
  const handleImportQRImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        
        if (code && code.data) {
          // Try Google Auth migration format
          const migrationEntries = parseGoogleAuthMigration(code.data);
          if (migrationEntries && migrationEntries.length > 0) {
            setImportedFromQR(prev => [...prev, ...migrationEntries]);
            // Close import modal and open scan modal to show imported entries
            setShowImportModal(false);
            setShowImportScanModal(true);
            return;
          }
          
          // Try standard otpauth format
          const parsed = parseOtpAuthUrl(code.data);
          if (parsed) {
            setImportedFromQR(prev => [...prev, parsed]);
            // Close import modal and open scan modal to show imported entries
            setShowImportModal(false);
            setShowImportScanModal(true);
          } else {
            alert(t('otp.invalidQR'));
          }
        } else {
          alert(t('otp.noQRFound'));
        }
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    // Reset input so same file can be selected again
    e.target.value = '';
  };
  
  // Confirm and save imported QR entries
  const confirmImportFromQR = () => {
    if (importedFromQR.length === 0) return;
    
    const newEntries = importedFromQR.map(entry => ({
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      ...entry,
      createdAt: new Date().toISOString()
    }));
    
    isDirtyRef.current = true;
    setOtpEntries(prev => [...prev, ...newEntries]);
    setImportedFromQR([]);
    setShowImportScanModal(false);
    alert(t('otp.importSuccess', { count: newEntries.length }));
  };
  
  // Add new OTP entry
  const addOtpEntry = (entry) => {
    const newOtp = {
      id: Date.now().toString(),
      ...entry,
      createdAt: new Date().toISOString()
    };
    isDirtyRef.current = true;
    setOtpEntries(prev => [...prev, newOtp]);
    setShowAddModal(false);
    setNewEntry({
      issuer: '',
      account: '',
      secret: '',
      type: 'totp',
      digits: 6,
      period: 30
    });
  };
  
  // Handle manual entry
  const handleManualAdd = () => {
    if (!newEntry.secret.trim()) {
      alert(t('otp.secretRequired'));
      return;
    }
    
    // Check if it's an otpauth URL
    if (newEntry.secret.startsWith('otpauth://')) {
      const parsed = parseOtpAuthUrl(newEntry.secret);
      if (parsed) {
        addOtpEntry(parsed);
        return;
      }
    }
    
    // Plain secret
    addOtpEntry({
      ...newEntry,
      secret: newEntry.secret.replace(/\s/g, '').toUpperCase()
    });
  };
  
  // Delete entry
  const deleteEntry = (id) => {
    if (window.confirm(t('otp.confirmDelete'))) {
      const newEntries = otpEntries.filter(e => e.id !== id);
      isDirtyRef.current = true;
      setOtpEntries(newEntries);
    }
  };
  
  // Copy code to clipboard
  const copyCode = (id, code) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };
  
  // Export entries
  const exportEntries = () => {
    const exportData = otpEntries.map(entry => ({
      type: entry.type,
      issuer: entry.issuer,
      account: entry.account,
      secret: entry.secret,
      algorithm: entry.algorithm || 'SHA1',
      digits: entry.digits,
      period: entry.period
    }));
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `otp-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  // Import entries from file
  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target.result;
        let imported = [];
        
        // Try to detect format
        if (content.trim().startsWith('{') || content.trim().startsWith('[')) {
          // JSON format
          const data = JSON.parse(content);
          
          // Google Authenticator export format
          if (data.payload) {
            // aegis or similar format
            imported = parseAegisFormat(data);
          } else if (Array.isArray(data)) {
            // Plain array format
            imported = data.map(item => ({
              id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
              type: item.type || 'totp',
              issuer: item.issuer || item.name || '',
              account: item.account || item.email || item.username || '',
              secret: item.secret || item.key || '',
              algorithm: item.algorithm || 'SHA1',
              digits: item.digits || 6,
              period: item.period || 30,
              createdAt: new Date().toISOString()
            }));
          }
        } else if (content.includes('otpauth://')) {
          // Text file with otpauth URLs
          const urls = content.match(/otpauth:\/\/[^\s]+/g) || [];
          imported = urls.map(url => {
            const parsed = parseOtpAuthUrl(url);
            if (parsed) {
              return {
                id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
                ...parsed,
                createdAt: new Date().toISOString()
              };
            }
            return null;
          }).filter(Boolean);
        }
        
        if (imported.length > 0) {
          isDirtyRef.current = true;
          setOtpEntries(prev => [...prev, ...imported]);
          alert(t('otp.importSuccess', { count: imported.length }));
        } else {
          alert(t('otp.importEmpty'));
        }
      } catch (err) {
        console.error('Import error:', err);
        alert(t('otp.importError'));
      }
    };
    reader.readAsText(file);
    setShowImportModal(false);
  };
  
  // Parse Aegis format
  const parseAegisFormat = (data) => {
    const entries = data.db?.entries || data.entries || [];
    return entries.map(item => ({
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      type: item.type?.toLowerCase() || 'totp',
      issuer: item.issuer || item.name || '',
      account: item.name || item.account || '',
      secret: item.info?.secret || item.secret || '',
      algorithm: item.info?.algo || item.algorithm || 'SHA1',
      digits: item.info?.digits || item.digits || 6,
      period: item.info?.period || item.period || 30,
      createdAt: new Date().toISOString()
    }));
  };
  
  // Filter entries
  const filteredEntries = otpEntries.filter(entry => {
    const query = searchQuery.toLowerCase();
    return (
      entry.issuer?.toLowerCase().includes(query) ||
      entry.account?.toLowerCase().includes(query)
    );
  });
  
  // Progress circle for time remaining
  const progress = (timeLeft / 30) * 100;
  
  // Show WebAuthn tab
  if (activeTab === 'webauthn') {
    return (
      <div className="otp-manager">
        <div className="otp-header">
          <h2><FiShield size={24} /> {t('otp.title')}</h2>
          <div className="otp-header-actions">
            <button className="btn-icon" onClick={onClose} title={t('common.close')}>
              <FiX size={18} />
            </button>
          </div>
        </div>
        
        <div className="otp-tabs">
          <button className={`tab ${activeTab === 'totp' ? 'active' : ''}`} onClick={() => setActiveTab('totp')}>
            <FiSmartphone size={16} /> TOTP
          </button>
          <button className={`tab ${activeTab === 'webauthn' ? 'active' : ''}`} onClick={() => setActiveTab('webauthn')}>
            <FiKey size={16} /> {t('otp.tabSecurityKeys')}
          </button>
        </div>
        
        <WebAuthnManager onClose={onClose} />
      </div>
    );
  }

  return (
    <div className="otp-manager">
      <div className="otp-header">
        <h2><FiShield size={24} /> {t('otp.title')}</h2>
        <div className="otp-header-actions">
          <button className="btn-icon" onClick={() => setShowImportModal(true)} title={t('otp.import')}>
            <FiDownload size={18} />
          </button>
          <button className="btn-icon" onClick={exportEntries} title={t('otp.export')}>
            <FiUpload size={18} />
          </button>
          <button className="btn-icon" onClick={onClose} title={t('common.close')}>
            <FiX size={18} />
          </button>
        </div>
      </div>
      
      <div className="otp-tabs">
        <button className={`tab ${activeTab === 'totp' ? 'active' : ''}`} onClick={() => setActiveTab('totp')}>
          <FiSmartphone size={16} /> TOTP
        </button>
        <button className={`tab ${activeTab === 'webauthn' ? 'active' : ''}`} onClick={() => setActiveTab('webauthn')}>
          <FiKey size={16} /> {t('otp.tabSecurityKeys')}
        </button>
      </div>
      
      <div className="otp-toolbar">
        <div className="otp-search">
          <FiSearch size={16} />
          <input
            type="text"
            placeholder={t('otp.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <button className="btn-primary" onClick={() => setShowAddModal(true)}>
          <FiPlus size={16} /> {t('otp.addNew')}
        </button>
      </div>
      
      <div className="otp-timer">
        <svg className="timer-circle" viewBox="0 0 36 36">
          <path
            className="timer-bg"
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
          />
          <path
            className="timer-progress"
            strokeDasharray={`${progress}, 100`}
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
          />
        </svg>
        <span className="timer-text">{timeLeft}s</span>
      </div>
      
      <div className="otp-list">
        {filteredEntries.length === 0 ? (
          <div className="otp-empty">
            <FiKey size={48} />
            <h3>{t('otp.noEntries')}</h3>
            <p>{t('otp.noEntriesDesc')}</p>
          </div>
        ) : (
          filteredEntries.map(entry => (
            <div key={entry.id} className="otp-entry">
              <div className="otp-entry-icon">
                <FiSmartphone size={24} />
              </div>
              <div className="otp-entry-info">
                <div className="otp-entry-issuer">{entry.issuer || t('otp.unknownIssuer')}</div>
                <div className="otp-entry-account">{entry.account}</div>
              </div>
              <div className="otp-entry-code">
                <span className={`code ${timeLeft <= 5 ? 'expiring' : ''}`}>
                  {codes[entry.id] ? 
                    `${codes[entry.id].slice(0, 3)} ${codes[entry.id].slice(3)}` : 
                    '--- ---'
                  }
                </span>
              </div>
              <div className="otp-entry-actions">
                <button
                  className={`btn-icon ${copiedId === entry.id ? 'copied' : ''}`}
                  onClick={() => copyCode(entry.id, codes[entry.id])}
                  title={t('otp.copy')}
                >
                  {copiedId === entry.id ? <FiCheck size={16} /> : <FiCopy size={16} />}
                </button>
                <button
                  className="btn-icon danger"
                  onClick={() => deleteEntry(entry.id)}
                  title={t('otp.delete')}
                >
                  <FiTrash2 size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* Add Entry Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content otp-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('otp.addNew')}</h3>
              <button onClick={() => setShowAddModal(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              <div className="add-options">
                <button className="add-option" onClick={() => { setShowAddModal(false); setShowScanModal(true); }}>
                  <FiCamera size={32} />
                  <span>{t('otp.scanQR')}</span>
                </button>
                <button className="add-option" onClick={() => fileInputRef.current?.click()}>
                  <FiImage size={32} />
                  <span>{t('otp.uploadQR')}</span>
                </button>
              </div>
              
              <div className="divider">{t('common.or')}</div>
              
              <div className="manual-entry">
                <h4>{t('otp.manualEntry')}</h4>
                <div className="form-group">
                  <label>{t('otp.issuer')}</label>
                  <input
                    type="text"
                    placeholder={t('otp.issuerPlaceholder')}
                    value={newEntry.issuer}
                    onChange={e => setNewEntry(prev => ({ ...prev, issuer: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>{t('otp.account')}</label>
                  <input
                    type="text"
                    placeholder={t('otp.accountPlaceholder')}
                    value={newEntry.account}
                    onChange={e => setNewEntry(prev => ({ ...prev, account: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>{t('otp.secret')} *</label>
                  <input
                    type="text"
                    placeholder={t('otp.secretPlaceholder')}
                    value={newEntry.secret}
                    onChange={e => setNewEntry(prev => ({ ...prev, secret: e.target.value }))}
                  />
                  <small>{t('otp.secretHelp')}</small>
                </div>
                <button className="btn-primary" onClick={handleManualAdd}>
                  <FiPlus /> {t('otp.addEntry')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Camera Scan Modal */}
      {showScanModal && (
        <div className="modal-overlay" onClick={() => { stopCamera(); setShowScanModal(false); }}>
          <div className="modal-content otp-modal scan-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('otp.scanQR')}</h3>
              <button onClick={() => { stopCamera(); setShowScanModal(false); }}><FiX /></button>
            </div>
            <div className="modal-body">
              <div className="camera-container">
                <video ref={videoRef} autoPlay playsInline />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                <div className="scan-overlay">
                  <div className="scan-frame"></div>
                </div>
              </div>
              <p className="scan-hint">{t('otp.scanHint')}</p>
              <button className="btn-secondary" onClick={startCamera}>
                <FiCamera /> {t('otp.startCamera')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal-content otp-modal import-modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('otp.import')}</h3>
              <button onClick={() => setShowImportModal(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              <p>{t('otp.importDescription')}</p>
              
              {/* QR Import Options */}
              <div className="import-qr-section">
                <h4><FiCamera /> {t('otp.importViaQR')}</h4>
                <p className="import-qr-desc">{t('otp.importQRDesc')}</p>
                <div className="import-qr-buttons">
                  <button 
                    className="btn-primary"
                    onClick={() => { setShowImportModal(false); setShowImportScanModal(true); }}
                  >
                    <FiCamera /> {t('otp.scanWithCamera')}
                  </button>
                  <button 
                    className="btn-secondary"
                    onClick={() => importQRInputRef.current?.click()}
                  >
                    <FiImage /> {t('otp.uploadQRImage')}
                  </button>
                </div>
              </div>
              
              <div className="import-divider">{t('common.or')}</div>
              
              {/* File Import Section */}
              <div className="import-sources">
                <h4><FiUpload /> {t('otp.importViaFile')}</h4>
                <div className="import-source">
                  <strong>Google Authenticator</strong>
                  <p>{t('otp.googleAuthHelp')}</p>
                </div>
                <div className="import-source">
                  <strong>Authy</strong>
                  <p>{t('otp.authyHelp')}</p>
                </div>
                <div className="import-source">
                  <strong>Microsoft Authenticator</strong>
                  <p>{t('otp.msAuthHelp')}</p>
                </div>
                <div className="import-source">
                  <strong>Aegis / andOTP</strong>
                  <p>{t('otp.aegisHelp')}</p>
                </div>
              </div>
              <input
                type="file"
                accept=".json,.txt"
                onChange={handleImportFile}
                style={{ display: 'none' }}
                id="import-file"
              />
              <label htmlFor="import-file" className="btn-primary upload-btn">
                <FiUpload /> {t('otp.selectFile')}
              </label>
            </div>
          </div>
        </div>
      )}
      
      {/* Import QR Scan Modal */}
      {showImportScanModal && (
        <div className="modal-overlay" onClick={() => { stopImportCamera(); setShowImportScanModal(false); setImportedFromQR([]); }}>
          <div className="modal-content otp-modal scan-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('otp.scanQRImport')}</h3>
              <button onClick={() => { stopImportCamera(); setShowImportScanModal(false); setImportedFromQR([]); }}><FiX /></button>
            </div>
            <div className="modal-body">
              <div className="camera-container">
                <video ref={importVideoRef} autoPlay playsInline />
                <canvas ref={importCanvasRef} style={{ display: 'none' }} />
                <div className="scan-overlay">
                  <div className="scan-frame"></div>
                </div>
              </div>
              
              <p className="scan-hint">{t('otp.scanImportHint')}</p>
              
              <div className="import-scan-actions">
                <button className="btn-secondary" onClick={startImportCamera}>
                  <FiCamera /> {t('otp.startCamera')}
                </button>
                <button className="btn-secondary" onClick={() => importQRInputRef.current?.click()}>
                  <FiImage /> {t('otp.uploadImage')}
                </button>
              </div>
              
              {/* Preview imported entries */}
              {importedFromQR.length > 0 && (
                <div className="import-preview">
                  <h4>{t('otp.scannedEntries', { count: importedFromQR.length })}</h4>
                  <div className="import-preview-list">
                    {importedFromQR.map((entry, index) => (
                      <div key={index} className="import-preview-item">
                        <FiKey />
                        <span className="entry-issuer">{entry.issuer || t('otp.unknownIssuer')}</span>
                        <span className="entry-account">{entry.account}</span>
                        <button 
                          className="btn-icon danger"
                          onClick={() => setImportedFromQR(prev => prev.filter((_, i) => i !== index))}
                          title={t('common.remove')}
                        >
                          <FiX size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="import-preview-actions">
                    <button className="btn-secondary" onClick={() => setImportedFromQR([])}>
                      {t('common.clear')}
                    </button>
                    <button className="btn-primary" onClick={confirmImportFromQR}>
                      <FiCheck /> {t('otp.importEntries', { count: importedFromQR.length })}
                    </button>
                  </div>
                </div>
              )}
              
              <p className="scan-multiqa-hint">{t('otp.multiQRHint')}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Hidden file input for QR image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        style={{ display: 'none' }}
      />
      
      {/* Hidden file input for Import QR image */}
      <input
        ref={importQRInputRef}
        type="file"
        accept="image/*"
        onChange={handleImportQRImage}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default OTPManager;
